import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, RotateCcw, Maximize2, LayoutList, Trash2, Check } from "lucide-react";

// Per-user (per account) column widths saved through the existing view-prefs API.
// `keys` lets tables with customizable columns remember widths per column; `pinned` columns keep their default width.
export function useColumnWidths(prefKey, defaults, minWidth = 56, keys = null, pinned = null) {
  const ids = keys || defaults.map((_, i) => String(i));
  const pin = pinned || [0, defaults.length - 1];
  const [saved, setSaved] = useState({});
  const saveT = useRef();
  useEffect(() => {
    api.get(`/my-profile/view-prefs/${prefKey}`).then((r) => {
      const w = r.data?.prefs?.widths;
      if (Array.isArray(w)) setSaved(Object.fromEntries(w.map((v, i) => [String(i), v])));
      else if (w && typeof w === "object") setSaved(w);
    }).catch(() => {});
  }, [prefKey]);
  const widths = defaults.map((d, i) => (pin.includes(i) ? d : Math.max(minWidth, Number(saved[ids[i]]) || d)));
  const persist = (m) => {
    clearTimeout(saveT.current);
    saveT.current = setTimeout(() => api.put(`/my-profile/view-prefs/${prefKey}`, { widths: m }).catch(() => {}), 400);
  };
  const apply = (arr) => setSaved((p) => { const m = { ...p }; arr.forEach((w, i) => { if (!pin.includes(i)) m[ids[i]] = w; }); persist(m); return m; });
  const setWidth = (i, v) => setSaved((p) => { const m = { ...p, [ids[i]]: Math.max(minWidth, Math.round(v)) }; persist(m); return m; });
  const reset = () => { setSaved({}); persist({}); };
  const restore = (m) => { setSaved(m || {}); persist(m || {}); };
  const fit = (avail) => {
    if (!avail) return;
    const fixed = pin.reduce((a, i) => a + (widths[i] || 0), 0);
    if (fixed > avail) return;
    let n = [...widths];
    for (let pass = 0; pass < 6; pass++) {
      const free = n.map((w, i) => (!pin.includes(i) && w > minWidth ? i : -1)).filter((i) => i >= 0);
      const over = n.reduce((a, b) => a + b, 0) - avail;
      const pool = free.reduce((a, i) => a + n[i], 0);
      if (Math.abs(over) < 1 || !pool) break;
      const k = Math.max(0, 1 - over / pool);
      n = n.map((w, i) => (free.includes(i) ? Math.max(minWidth, Math.floor(w * k)) : w));
    }
    apply(n);
  };
  return { widths, saved, restore, setWidth, reset, fit, total: widths.reduce((a, b) => a + b, 0) };
}

// Drag handle placed inside a <th>. Pointer events are captured so sorting/editing never fires.
export function ResizeHandle({ index, width, onResize, label }) {
  const start = (e) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX, w0 = width;
    const move = (ev) => onResize(index, w0 + ev.clientX - x0);
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); document.body.style.cursor = ""; };
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const key = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") { e.preventDefault(); e.stopPropagation(); onResize(index, width + (e.key === "ArrowRight" ? 16 : -16)); }
  };
  return (
    <span role="separator" aria-orientation="vertical" aria-label={`Resize ${label || "column"}`} tabIndex={0}
      onPointerDown={start} onKeyDown={key} onClick={(e) => e.stopPropagation()} data-testid={`col-resize-${index}`}
      className="absolute right-0 top-0 h-full w-2 cursor-col-resize select-none touch-none group/rh flex justify-center focus:outline-none">
      <span className="w-px h-full bg-slate-200 group-hover/rh:bg-brand group-focus/rh:bg-brand group-hover/rh:w-0.5" />
    </span>
  );
}

// Toolbar (left/right + reset/fit) above a bounded scroll area: the header sticks to the top of the
// area and the horizontal scrollbar stays at the bottom of the visible area, never only after the last row.
// Named, per-user layouts (column widths + optional extra state such as visible columns).
function SavedViews({ prefKey, cols, getExtra, applyExtra, testId }) {
  const [views, setViews] = useState([]);
  const [active, setActive] = useState("");
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/my-profile/view-prefs/${prefKey}`).then((r) => {
      setViews(r.data?.prefs?.views || []); setActive(r.data?.prefs?.active || "");
    }).catch(() => {});
  }, [prefKey]);
  const store = (v, a) => {
    setViews(v); setActive(a);
    api.put(`/my-profile/view-prefs/${prefKey}`, { views: v, active: a }).catch(() => toast.error("Could not save views"));
  };
  const add = () => {
    const n = name.trim().slice(0, 40);
    if (!n) return;
    const rest = views.filter((x) => x.name.toLowerCase() !== n.toLowerCase());
    if (rest.length >= 20) { toast.error("You can keep up to 20 saved views"); return; }
    store([...rest, { name: n, widths: cols.saved, ...(getExtra ? { extra: getExtra() } : {}) }], n);
    setName(""); toast.success(`Saved view "${n}"`);
  };
  const apply = (v) => {
    cols.restore(v.widths);
    if (applyExtra && v.extra) applyExtra(v.extra);
    store(views, v.name); setOpen(false);
  };
  const useDefault = () => { cols.reset(); if (applyExtra) applyExtra({}); store(views, ""); setOpen(false); };
  const del = (n) => store(views.filter((x) => x.name !== n), active === n ? "" : active);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 max-w-[220px]" data-testid={`${testId}-views`}>
          <LayoutList className="w-3.5 h-3.5 mr-1 shrink-0" /><span className="truncate">View: {active || "Default"}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-white w-72 p-2" data-testid={`${testId}-views-menu`}>
        <p className="px-2 pt-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Saved views</p>
        <button type="button" onClick={useDefault} data-testid={`${testId}-view-default`}
          className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
          <Check className={`w-3.5 h-3.5 ${active ? "opacity-0" : "text-brand"}`} />Default layout
        </button>
        {views.map((v) => (
          <div key={v.name} className="flex items-center gap-1 rounded-md hover:bg-slate-50">
            <button type="button" onClick={() => apply(v)} data-testid={`${testId}-view-${v.name}`}
              className="flex-1 min-w-0 flex items-center gap-2 px-2 py-1.5 text-sm text-slate-700 text-left">
              <Check className={`w-3.5 h-3.5 shrink-0 ${active === v.name ? "text-brand" : "opacity-0"}`} /><span className="truncate">{v.name}</span>
            </button>
            <button type="button" onClick={() => del(v.name)} aria-label={`Delete view ${v.name}`} data-testid={`${testId}-view-delete-${v.name}`}
              className="p-1.5 text-slate-300 hover:text-rose-500"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <div className="mt-2 pt-2 border-t border-slate-100">
          <p className="px-1 pb-1 text-xs text-slate-500">Save the current layout as a view</p>
          <div className="flex gap-1">
            <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="e.g. Phone outreach" maxLength={40} className="h-8 text-sm" data-testid={`${testId}-view-name`} />
            <Button size="sm" className="h-8 bg-brand hover:bg-brand-strong text-white" onClick={add} disabled={!name.trim()} data-testid={`${testId}-view-save`}>Save</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ScrollTableShell({ children, cols, testId = "table-shell", maxHeight = "calc(100vh - 12rem)", viewsKey, getExtra, applyExtra }) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ left: true, right: true });
  const update = useCallback(() => {
    const el = ref.current; if (!el) return;
    setEdge({ left: el.scrollLeft <= 1, right: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 });
  }, []);
  useEffect(() => {
    update();
    const el = ref.current; if (!el) return undefined;
    const ro = new ResizeObserver(update); ro.observe(el); if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [update, cols.total]);
  const page = (dir) => ref.current?.scrollBy({ left: dir * ref.current.clientWidth * 0.7, behavior: "smooth" });
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-slate-100">
        <div className="flex items-center gap-1">
          {viewsKey && <SavedViews prefKey={viewsKey} cols={cols} getExtra={getExtra} applyExtra={applyExtra} testId={testId} />}
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={edge.left} onClick={() => page(-1)} aria-label="Scroll table left" data-testid={`${testId}-left`}><ChevronLeft className="w-4 h-4" /><span className="hidden sm:inline ml-1">Left</span></Button>
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={edge.right} onClick={() => page(1)} aria-label="Scroll table right" data-testid={`${testId}-right`}><span className="hidden sm:inline mr-1">Right</span><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-8 text-slate-600" onClick={() => cols.fit(ref.current?.clientWidth - 2)} data-testid={`${testId}-fit`}><Maximize2 className="w-3.5 h-3.5 mr-1" />Fit Columns to Width</Button>
          <Button size="sm" variant="ghost" className="h-8 text-slate-600" onClick={cols.reset} data-testid={`${testId}-reset`}><RotateCcw className="w-3.5 h-3.5 mr-1" />Reset Column Widths</Button>
        </div>
      </div>
      <div ref={ref} onScroll={update} tabIndex={0} aria-label="Table, use arrow keys or Shift+scroll to move sideways"
        className="overflow-auto tr-scroll focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/30 rounded-b-xl" style={{ maxHeight }} data-testid={`${testId}-scroll`}>
        {children}
      </div>
    </div>
  );
}
